import { Component, Injectable, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, Validators } from "@angular/forms";
import { MarkdownModule } from "ngx-markdown";
import { BehaviorSubject, tap } from "rxjs";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzPanelComponent } from "@template-ui/components/ez-panel/panel/panel.component";
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { AppRoutingBaseComponent } from "@app/shared/components/app-routing-base.component";
import { DynamicFormDraftConfig, InputConfig } from "@template-ui/types/ez-form.types";
import { DocMeta } from "@template-ui/decorators";
import { FormValuesObject } from "@template-ui/types/ez-form.types";
import { EzFormInputType, NamedEvent, PanelHeaderConfig } from "@template-ui/types";
import { toSignal } from "@angular/core/rxjs-interop";
import { EZFormDraftService, DraftEnvelope } from "../../../../services/form-draft.service";

interface DraftLogEntry {
  type: "load" | "save" | "clear";
  key: string;
  timestamp: string;
  payload?: FormValuesObject;
  note?: string;
}

@Injectable()
class LoggingDynamicFormDraftService extends EZFormDraftService {
  private readonly eventsSubject = new BehaviorSubject<DraftLogEntry[]>([]);
  readonly events$ = this.eventsSubject.asObservable();

  override load<T = unknown>(key: string) {
    return super.load<T>(key).pipe(
      tap(draft => {
        const payload = (draft as DraftEnvelope<FormValuesObject> | undefined)?.payload;
        this.pushEvent({
          type: "load",
          key,
          timestamp: new Date().toISOString(),
          payload: this.clonePayload(payload),
          note: payload ? "Draft restored from mock endpoint." : "No draft found for this key."
        });
      })
    );
  }

  override save<T = unknown>(envelope: DraftEnvelope<T>) {
    return super.save(envelope).pipe(
      tap(() => {
        this.pushEvent({
          type: "save",
          key: envelope.draftKey,
          timestamp: new Date().toISOString(),
          payload: this.clonePayload(envelope.payload as FormValuesObject),
          note: "Draft snapshot saved via DynamicFormDraftService."
        });
      })
    );
  }

  override clear(key: string) {
    return super.clear(key).pipe(
      tap(() => {
        this.pushEvent({
          type: "clear",
          key,
          timestamp: new Date().toISOString(),
          note: "Draft cleared."
        });
      })
    );
  }

  private pushEvent(entry: DraftLogEntry) {
    const updated = [entry, ...this.eventsSubject.value].slice(0, 20);
    this.eventsSubject.next(updated);
  }

  private clonePayload(payload?: FormValuesObject): FormValuesObject | undefined {
    if (!payload) {
      return undefined;
    }
    const cloneFn = (globalThis as any)?.structuredClone as (<T>(value: T) => T) | undefined;
    if (typeof cloneFn === "function") {
      return cloneFn(payload);
    }
    return JSON.parse(JSON.stringify(payload));
  }
}

@DocMeta({
  hide: true,
  showcaseTitle: "Draft Sample",
  showcaseDesc: `
## Overview

- Drafts capture **dirty + invalid** form states so users can safely step away mid-edit.
- When the form becomes valid, the primary \`saveEndpoint\` handles persistence and the stored draft should be cleared.
- Each draft entry is scoped by \`draftConfig.key\` and the current user (or optional \`ownerId\` override).

## Configuration

| Option        | Purpose                                                                 | Default behaviour                         |
|---------------|-------------------------------------------------------------------------|-------------------------------------------|
| \`key\`         | Unique identifier for the draft entry                                   | Derived from route + form + record id     |
| \`debounceMs\`  | Delay before saving a draft after a change                              | \`1200\` ms                                  |
| \`autoLoad\`    | Load the draft on render                                                | \`true\`                                    |
| \`ownerId\`     | Explicit draft owner                                                    | Current user id                           |

### Save Flow

1. Initial render: the component attempts to restore an existing draft. If none exists, server data is loaded.
2. While the form is **dirty** and **invalid**, snapshots are written through \`DynamicFormDraftService\`.
3. Once the form is valid and the regular autosave executes, the draft should be cleared so the next visit begins from the authoritative record.

The panels below allow you to exercise the flow and inspect what the draft service stores.
  `
})
@Component({
    selector: "app-dynamic-form-draft-sample",
    imports: [
        CommonModule,
        FormsModule,
        MarkdownModule,
        UiLibraryModule,
        EzPanelComponent,
        EzFormComponent,
    ],
    templateUrl: "./dynamic-form-draft-sample.component.html",
    styleUrl: "./dynamic-form-draft-sample.component.scss",
    providers: [
        { provide: EZFormDraftService, useClass: LoggingDynamicFormDraftService }
    ]
})
export class DynamicFormDraftSampleComponent extends AppRoutingBaseComponent {
  private readonly draftService = inject(EZFormDraftService) as LoggingDynamicFormDraftService;

  formValues: FormValuesObject = {};
  saveEndpoint = "mock/FormDraftSample/{id}";

  draftConfig: DynamicFormDraftConfig = {
    key: "bits-dynamic-form-draft-sample",
    debounceMs: 800,
    autoLoad: true
  };

  draftEvents = toSignal(this.draftService.events$, { initialValue: [] as DraftLogEntry[] });
  lastAutosavePayload = signal<FormValuesObject | undefined>(undefined);
  formValid = signal<boolean>(false);
  apiOptions = { feedbackOptions: { successMode: "greenCheckmarks" } };

  additionalActions: InputConfig[] = [
    {
      type: EzFormInputType.BUTTON,
      name: 'resetForm',
      buttonText: 'Reset Form',
    },
    {
      type: EzFormInputType.BUTTON,
      name: 'clearDraft',
      buttonText: 'Clear Draft',
    },
  ];

  panelHeaderConfig: PanelHeaderConfig = {
    additionalActions: this.additionalActions,
  };

  sampleConfig: InputConfig[] = [
    {
      type: EzFormInputType.TEXT,
      name: "firstName",
      label: "First Name",
      width: "1x",
      validation: [Validators.required],
      validationMessages: { required: "First name is required." }
    },
    {
      type: EzFormInputType.TEXT,
      name: "lastName",
      label: "Last Name",
      width: "1x",
      validation: [Validators.required],
      validationMessages: { required: "Last name is required." }
    },
    {
      type: EzFormInputType.TEXT,
      name: "contactEmail",
      label: "Contact Email",
      width: "1x",
      validation: [Validators.email],
      validationMessages: { email: "Please enter a valid email address." }
    },
    {
      type: EzFormInputType.TEXTAREA,
      name: "notes",
      label: "Notes",
      width: "2x",
    },
    {
      type: EzFormInputType.HIDDEN,
      name: "id"
    }
  ];

  constructor() {
    super();

    this.initializeValues();
  }

  onFormUpdated(payload: any) {
    this.lastAutosavePayload.set(this.normalizeFormPayload(payload));
  }

  onFormStatusChanged(isValid: boolean) {
    this.formValid.set(isValid);
  }

  clearDraft() {
    if (this.draftConfig?.key) {
      this.draftService.clear(this.draftConfig.key).pipe(this.takeUntilDestroyed()).subscribe(() => {
        this.lastAutosavePayload.set(undefined);
      });
    }
  }

  resetFormValues() {
    this.loadInitialValues();
    this.lastAutosavePayload.set(undefined);
  }

  private initializeValues(): void {
    if (this.draftConfig?.key) {
      this.draftService.load<FormValuesObject>(this.draftConfig.key)
        .pipe(this.takeUntilDestroyed())
        .subscribe(draft => {
          if (draft?.payload) {
            this.formValues = { ...draft.payload };
            return;
          }
          this.loadInitialValues();
        });
      return;
    }
    this.loadInitialValues();
  }

  private loadInitialValues(): void {
    this.crudService.GetList("mock/FormDraftSample")
      .pipe(this.takeUntilDestroyed())
      .subscribe(resp => {
        const data = resp?.data ?? [];
        const first = data[0] ?? {
          id: "draft-sample-001",
          firstName: "",
          lastName: "",
          contactEmail: "",
          notes: ""
        };
        this.formValues = { ...first };
      });
  }

  private normalizeFormPayload(payload: any): FormValuesObject | undefined {
    if (!payload) {
      return undefined;
    }

    const candidate = typeof payload.getRawValue === "function"
      ? payload.getRawValue()
      : typeof payload.value === "function"
        ? payload.value()
        : payload.value ?? payload;

    try {
      return JSON.parse(JSON.stringify(candidate ?? {}));
    } catch {
      return { ...(candidate ?? {}) };
    }
  }

  onPanelHeaderButtonClick(e: NamedEvent) {

    switch (e.name) {
      case "resetForm":
        this.resetFormValues();
        break;

      case "clearDraft":
        this.clearDraft();
        break;
    }
  }
}
